import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { GET_CUSTOMER_ORDERS, GET_COMPLETED_ORDERS } from "src/constants/queries";
import { UPDATE_CUSTOMER_ORDER } from "src/constants/mutations";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { CustomerOrder, UpdateCustomerOrderInput } from "src/interfaces/order.interface";
import { OrderListNavigationDirectionEnum, OrderStatusEnum, OrderTypeEnum } from "src/enums/order.enum";
import { CustomerOrderEdge, PageInfo } from "src/interfaces/orders-props.interface";

const DEFAULT_PAGE_SIZE = 10;

export interface UseCompletedOrdersOptions {
  controlledSearchQuery?: string;
  controlledOrderTypeFilter?: OrderTypeEnum | null;
  from?: string;
  to?: string;
  onSearchChange?: (value: string) => void;
  onOrderTypeFilterChange?: (type: OrderTypeEnum | null) => void;
}

export function useCompletedOrders(first = DEFAULT_PAGE_SIZE, options?: UseCompletedOrdersOptions) {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();

  const [completedOrderEdges, setCompletedOrderEdges] = useState<CustomerOrderEdge[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCustomerOrdersLoading, setIsCustomerOrdersLoading] = useState(false);
  const [isUpdatingCustomerOrder, setIsUpdatingCustomerOrder] = useState(false);
  const [useFallbackQuery, setUseFallbackQuery] = useState(false);

  const [selectedCustomerOrder, setSelectedCustomerOrder] = useState<CustomerOrder | null>(null);
  const [selectedCustomerOrderIndex, setSelectedCustomerOrderIndex] = useState<number | null>(null);
  const [showCompletedOrderDrawer, setShowCompletedOrderDrawer] = useState(false);
  const [searchOrderQuery, setSearchOrderQuery] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState<OrderTypeEnum | null>(null);

  const effectiveSearch = options?.controlledSearchQuery !== undefined ? options.controlledSearchQuery : searchOrderQuery;
  const effectiveOrderType = options?.controlledOrderTypeFilter !== undefined ? options.controlledOrderTypeFilter : orderTypeFilter;
  const effectiveFrom = options?.from;
  const effectiveTo = options?.to;

  /** Tracks prior server filters so we can reset cursors when they change (server pagination must restart at page 1). */
  const previousEffectiveOrderType = useRef<OrderTypeEnum | null | undefined>(undefined);
  const previousEffectiveFrom = useRef<string | undefined>(undefined);
  const previousEffectiveTo = useRef<string | undefined>(undefined);

  const fetchCompletedOrders = useCallback(
    async (_isUpdate = false) => {
      if (!hasRestaurant?.correlationId) return;
      setIsCustomerOrdersLoading(true);
      try {
        const orderTypeChanged =
          previousEffectiveOrderType.current !== undefined &&
          previousEffectiveOrderType.current !== effectiveOrderType;
        const rangeChanged =
          previousEffectiveFrom.current !== undefined &&
          (previousEffectiveFrom.current !== effectiveFrom || previousEffectiveTo.current !== effectiveTo);
        previousEffectiveOrderType.current = effectiveOrderType;
        previousEffectiveFrom.current = effectiveFrom;
        previousEffectiveTo.current = effectiveTo;

        const shouldResetPagination = orderTypeChanged || rangeChanged;
        const effectiveAfter = shouldResetPagination ? null : after;
        const effectiveBefore = shouldResetPagination ? null : before;
        if (shouldResetPagination) {
          setAfter(null);
          setBefore(null);
          setCurrentPage(1);
        }

        const variables: Record<string, unknown> = {
          storeCorrelationId: hasRestaurant.correlationId,
          first: effectiveBefore ? undefined : first,
          last: effectiveBefore ? first : undefined,
          after: effectiveAfter ?? undefined,
          before: effectiveBefore ?? undefined,
        };

        if (!useFallbackQuery) {
          if (effectiveFrom && effectiveTo) {
            variables.startTimeInEpochMilli = new Date(effectiveFrom).getTime();
            variables.endTimeInEpochMilli = new Date(effectiveTo).getTime();
          }
          variables.excludeOrderStatus = "OPEN";
          variables.orderType = effectiveOrderType ?? null;
        }

        const query = useFallbackQuery ? GET_CUSTOMER_ORDERS : GET_COMPLETED_ORDERS;
        const { data: customerOrders } = await axBe.post(GRAPHQL_ENDPOINT, {
          query,
          variables: useFallbackQuery
            ? { storeCorrelationId: hasRestaurant.correlationId, first: variables.first, after: variables.after, last: variables.last, before: variables.before }
            : variables,
        });

        const rawEdges: CustomerOrderEdge[] =
          customerOrders?.data?.customerOrdersByStoreCorrelationId?.edges ?? [];
        const newPageInfo: PageInfo =
          customerOrders?.data?.customerOrdersByStoreCorrelationId?.pageInfo ?? null;

        const edges = useFallbackQuery
          ? rawEdges.filter((e: CustomerOrderEdge) => {
              const status = e?.node?.orderStatus;
              return status !== OrderStatusEnum.OPEN && status !== OrderStatusEnum.READY;
            })
          : rawEdges;

        setCompletedOrderEdges(edges);
        setPageInfo(newPageInfo);
      } catch (err) {
        console.error("Error fetching completed orders:", err);
        if (!useFallbackQuery) {
          setUseFallbackQuery(true);
        }
      } finally {
        setIsCustomerOrdersLoading(false);
      }
    },
    [hasRestaurant?.correlationId, first, after, before, useFallbackQuery, axBe, effectiveOrderType, effectiveFrom, effectiveTo]
  );

  const loadNextPage = useCallback(() => {
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      setAfter(pageInfo.endCursor);
      setBefore(null);
      setCurrentPage((p) => p + 1);
    }
  }, [pageInfo?.hasNextPage, pageInfo?.endCursor]);

  const loadPrevPage = useCallback(() => {
    if (pageInfo?.hasPreviousPage && pageInfo?.startCursor) {
      setBefore(pageInfo.startCursor);
      setAfter(null);
      setCurrentPage((p) => Math.max(1, p - 1));
    }
  }, [pageInfo?.hasPreviousPage, pageInfo?.startCursor]);

  const goToFirstPage = useCallback(() => {
    setAfter(null);
    setBefore(null);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (hasRestaurant) {
      fetchCompletedOrders();
    }
  }, [hasRestaurant, after, before, useFallbackQuery, fetchCompletedOrders]);

  const handleUpdateOrder = async (updatedOrder: UpdateCustomerOrderInput): Promise<boolean> => {
    setIsUpdatingCustomerOrder(true);
    try {
      const { data: updateCustomerOrder } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_CUSTOMER_ORDER,
        variables: { updateCustomerOrderInput: updatedOrder },
      });
      if (updateCustomerOrder?.data?.updateCustomerOrder) {
        setShowCompletedOrderDrawer(false);
        await fetchCompletedOrders(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error updating order:", err);
      setShowCompletedOrderDrawer(false);
      return false;
    } finally {
      setIsUpdatingCustomerOrder(false);
    }
  };

  /** Order type is applied on the server; only search is client-side on the current page. */
  const filteredCustomersOrder = useMemo(() => {
    let list = completedOrderEdges;
    if (effectiveSearch.trim()) {
      const q = effectiveSearch.toLowerCase();
      list = list.filter(
        (e: CustomerOrderEdge) =>
          e?.node?.customerFirstName?.toLowerCase().includes(q) ||
          (e?.node?.customerEmail ?? "")?.toLowerCase().includes(q) ||
          (e?.node?.customerPhoneNumber ?? "")?.includes(effectiveSearch) ||
          (e?.node?.correlationId ?? "")?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [completedOrderEdges, effectiveSearch]);

  const searchOrders = useCallback(
    (orderQuery: string) => {
      if (options?.onSearchChange) options.onSearchChange(orderQuery);
      else setSearchOrderQuery(orderQuery);
    },
    [options?.onSearchChange]
  );

  const setOrderTypeFilterSetter = useCallback(
    (type: OrderTypeEnum | null) => {
      if (options?.onOrderTypeFilterChange) options.onOrderTypeFilterChange(type);
      else setOrderTypeFilter(type);
    },
    [options?.onOrderTypeFilterChange]
  );

  const manageOrderCardDetail = (orderListNavigationDirection: OrderListNavigationDirectionEnum) => {
    if (filteredCustomersOrder.length === 0 || selectedCustomerOrderIndex == null) return;
    if (
      orderListNavigationDirection === OrderListNavigationDirectionEnum.INCREMENT &&
      selectedCustomerOrderIndex < filteredCustomersOrder.length - 1
    ) {
      setSelectedCustomerOrder(filteredCustomersOrder[selectedCustomerOrderIndex + 1].node);
      setSelectedCustomerOrderIndex(selectedCustomerOrderIndex + 1);
    }
    if (
      orderListNavigationDirection === OrderListNavigationDirectionEnum.DECREMENT &&
      selectedCustomerOrderIndex > 0
    ) {
      setSelectedCustomerOrder(filteredCustomersOrder[selectedCustomerOrderIndex - 1].node);
      setSelectedCustomerOrderIndex(selectedCustomerOrderIndex - 1);
    }
  };

  const handleRowClick = (customerOrder: CustomerOrder, index: number) => {
    setSelectedCustomerOrder(customerOrder);
    setSelectedCustomerOrderIndex(index);
    setShowCompletedOrderDrawer(true);
  };

  return {
    completedOrderEdges,
    filteredCustomersOrder,
    selectedCustomerOrderIndex,
    pageInfo,
    currentPage,
    isCustomerOrdersLoading,
    showCompletedOrderDrawer,
    selectedCustomerOrder,
    handleUpdateOrder,
    searchOrders,
    orderTypeFilter: effectiveOrderType,
    setOrderTypeFilter: setOrderTypeFilterSetter,
    manageOrderCardDetail,
    handleRowClick,
    setAfter,
    setBefore,
    setShowCompletedOrderDrawer,
    isUpdatingCustomerOrder,
    loadNextPage,
    loadPrevPage,
    goToFirstPage,
    fetchCompletedOrders,
  };
}
